import { Suspense, lazy } from 'react';
import { Navigate, BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import { getLocalStorage, setLocalStorage } from './common/utils';
import { useAuth } from '../scripts/fb';
import { useEffect, useState } from 'react';
import { Layout } from './common/components/Layout/v3/pages/layout';
import { useWorkspaceStore } from './common/lib/store/v3/layout';
import { Loader } from './common/components/UI/loader';

// Lazy load components
const Login = lazy(() =>
  import('./common/components/login').then((module) => ({ default: module.Login }))
);
const ErrorPage = lazy(() =>
  import('./common/components/ErrorBoundary').then((module) => ({ default: module.ErrorPage }))
);
const SignUp = lazy(() =>
  import('./common/components/login/sign-up').then((module) => ({ default: module.SignUp }))
);
const VerifyEmail = lazy(() =>
  import('./common/components/login/verify-email').then((module) => ({
    default: module.VerifyEmail
  }))
);
const NewFile = lazy(() =>
  import('./common/components/Layout/v3/pages/new-file').then((module) => ({
    default: module.NewFile
  }))
);
const MyFilesLayout = lazy(() =>
  import('./common/components/Layout/v3/pages/my-files').then((module) => ({
    default: module.MyFilesLayout
  }))
);
const NotFoundPage = lazy(() =>
  import('./common/components/NotFoundPage').then((module) => ({ default: module.NotFoundPage }))
);
const AIChat = lazy(() =>
  import('./common/components/Layout/v3/pages/ai-chat').then((module) => ({
    default: module.AIChat
  }))
);
const Distributions = lazy(() =>
  import('./common/components/Layout/v3/pages/distributions').then((module) => ({
    default: module.Distributions
  }))
);
const ControlTower = lazy(() =>
  import('./common/components/Layout/v3/pages/control-tower').then((module) => ({
    default: module.ControlTower
  }))
);
const Comparative = lazy(() =>
  import('./common/components/Layout/v3/pages/comparative').then((module) => ({
    default: module.Comparative
  }))
);
const Preditive = lazy(() =>
  import('./common/components/Layout/v3/pages/predictive').then((module) => ({
    default: module.Preditive
  }))
);
const Configuration = lazy(() =>
  import('./common/components/Layout/v3/pages/configuration').then((module) => ({
    default: module.Configuration
  }))
);
const MyDashboards = lazy(() =>
  import('./common/components/Layout/v3/pages/my-dashboards').then((module) => ({
    default: module.MyDashboards
  }))
);
const Exploratory = lazy(() =>
  import('./common/components/Layout/v3/pages/explanatory').then((module) => ({
    default: module.Exploratory
  }))
);
const Correlations = lazy(() =>
  import('./common/components/Layout/v3/pages/correlations').then((module) => ({
    default: module.Correlations
  }))
);
const Faqs = lazy(() =>
  import('./common/components/Layout/v3/pages/faqs').then((module) => ({ default: module.Faqs }))
);
const DataImport = lazy(() =>
  import('./common/components/Layout/v3/pages/data-import').then((module) => ({
    default: module.DataImport
  }))
);
const Upgrade = lazy(() =>
  import('./common/components/Layout/v3/pages/upgrade').then((module) => ({
    default: module.Upgrade
  }))
);
const Trends = lazy(() =>
  import('./common/components/Layout/app/frame/trends').then((module) => ({
    default: module.Trends
  }))
);
const SignUpFree = lazy(() =>
  import('./common/components/login/sign-up-free').then((module) => ({
    default: module.SignUpFree
  }))
);

export const App = () => {
  const auth = useAuth();
  const [token, setToken] = useState<any>(null);
  const stillTrialExpired = getLocalStorage('trial-expired');
  const currentWorkspace = useWorkspaceStore((state) => state.currentWorkspace);

  useEffect(() => {
    const getToken = async () => {
      const path = window.location.pathname;
      if (
        auth?.user &&
        path !== '/sign_in' &&
        path !== '/sign_up' &&
        path !== '/signup' &&
        path !== '/email_verification'
      ) {
        const tok = await auth?.user?.getIdToken();
        setToken(tok);
        setLocalStorage('token', tok);
      } else setToken(getLocalStorage('token'));
    };

    getToken();
  }, [auth?.user]);

  const RootRedirect = () => {
    const tokenStr = getLocalStorage('token');
    return tokenStr ? <Navigate to="/new_file" replace /> : <Navigate to="/sign_in" replace />;
  };

  const ProtectedNewFile = () => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

    useEffect(() => {
      const tokenStr = getLocalStorage('token');
      const isVerified = tokenStr || auth?.user?.emailVerified;
      setIsAuthenticated(!!isVerified);
    }, [auth, auth?.user]);
    if (isAuthenticated === null) return null;
    return isAuthenticated ? <NewFile /> : <Navigate to="/sign_in" replace />;
  };

  const ProtectedLayout = () => {
    const tokenStr = getLocalStorage('token');
    const hasChatUrl = window.location.href.includes('/chat');
    if (hasChatUrl && !currentWorkspace) {
      setLocalStorage('workspaceIdFromEmail', window.location.href.split('/chat/')[1]);
      setLocalStorage('sharedViewfromEmail', true);
    } else if (!window.location.href.includes('/new_file')) {
      localStorage.removeItem('workspaceIdFromEmail');
      localStorage.removeItem('sharedViewfromEmail');
    }
    return tokenStr ? <Layout /> : <Navigate to="/sign_in" replace />;
  };

  return (
    <Router>
      <Suspense
        fallback={
          <div className="flex justify-center light-black items-center h-screen font-montserrat">
            <Loader showText={false}></Loader>
          </div>
        }>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route
            path="/email_verification"
            element={<VerifyEmail />}
            errorElement={<ErrorPage>An error occurred</ErrorPage>}
          />
          <Route
            path="/sign_in"
            element={<Login />}
            errorElement={<ErrorPage>An error occurred</ErrorPage>}
          />
          <Route
            path="/sign_up"
            element={<SignUp />}
            errorElement={<ErrorPage>An error occurred</ErrorPage>}
          />
          <Route
            path="/signup"
            element={<SignUpFree />}
            errorElement={<ErrorPage>An error occurred</ErrorPage>}
          />
          <Route element={<ProtectedLayout />}>
            <Route path="/new_file" element={<ProtectedNewFile />} />
            <Route
              path="/files"
              errorElement={<ErrorPage>An error occurred</ErrorPage>}
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <MyFilesLayout />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/chat/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <AIChat />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/control-tower/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <ControlTower />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/distributions/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <Distributions />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/comparative/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <Comparative />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/predictive/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <Preditive />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/configuration/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <Configuration />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/dashboards/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <MyDashboards />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/exploratory/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <Exploratory />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/correlations/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <Correlations />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/trends/:workspaceid"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <Trends hasv3={true} />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/faqs"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <Faqs />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/upgrade"
              element={
                auth?.user?.emailVerified || stillTrialExpired || getLocalStorage('token') ? (
                  <Upgrade />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
            <Route
              path="/connectors"
              element={
                auth?.user?.emailVerified || getLocalStorage('token') ? (
                  <DataImport />
                ) : (
                  <Navigate to="/sign_in" replace />
                )
              }
            />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </Router>
  );
};

export default App;
